/**
 * ============================================================================
 *   Copyright (c) 2026 Dobler Data Solutions, LLC. ALL RIGHTS RESERVED.
 * ============================================================================
 *   Module       : Tabs
 *   Feature      : 197-portal-light-theme-responsive (design blueprint)
 *   Description  : Canonical token-underline tab bar. Replaces the ~20 hand-
 *                  rolled tab bars across the portal so every tabbed page looks
 *                  and behaves identically (active accent underline, optional
 *                  icon + count chip). Controlled via `value` / `onChange`.
 *   Blueprint    : docs/design-blueprint.md
 *   Constitution : v1.19.0
 *   Author       : Peter Dobler
 * ============================================================================
 */
import React from 'react';
import { clsx } from 'clsx';

export interface TabItem {
  key: string;
  label: React.ReactNode;
  icon?: React.ReactNode;
  /** Optional count chip (e.g. result count). */
  count?: number | string;
  disabled?: boolean;
}

export interface TabsProps {
  tabs: TabItem[];
  value: string;
  onChange: (key: string) => void;
  className?: string;
}

/** Canonical tab bar. Use instead of hand-rolling a `border-b-2` underline row. */
export const Tabs: React.FC<TabsProps> = ({ tabs, value, onChange, className }) => (
  // overflow-y-hidden: `overflow-x-auto` alone makes overflow-y compute to auto, and the tabs' `-mb-px`
  // overhang the list by one pixel, which drew a one-pixel vertical scrollbar on Windows (2026-10-13).
  <div role="tablist" className={clsx('flex gap-1 overflow-x-auto overflow-y-hidden border-b border-[var(--color-border)]', className)}>
    {tabs.map((t) => {
      const active = t.key === value;
      return (
        <button
          key={t.key}
          role="tab"
          type="button"
          aria-selected={active}
          disabled={t.disabled}
          onClick={() => onChange(t.key)}
          className={clsx(
            '-mb-px inline-flex flex-shrink-0 items-center gap-2 whitespace-nowrap border-b-2 px-4 py-2.5 text-sm font-medium transition-colors',
            'disabled:cursor-not-allowed disabled:opacity-50',
            active
              ? 'border-[var(--color-accent)] text-[var(--color-accent)]'
              : 'border-transparent text-[var(--color-text-secondary)] hover:text-[var(--color-text-primary)]',
          )}
        >
          {t.icon}
          <span>{t.label}</span>
          {t.count != null && (
            <span
              className={clsx(
                'rounded-[var(--radius-pill)] px-1.5 py-0.5 text-[11px] font-semibold leading-none',
                active
                  ? 'bg-[var(--color-accent)] text-white'
                  : 'bg-[var(--color-bg-subtle)] text-[var(--color-text-secondary)]',
              )}
            >
              {t.count}
            </span>
          )}
        </button>
      );
    })}
  </div>
);

export default Tabs;
